import { useEffect, useState } from "react";

// Cached IP to avoid multiple API calls
let cachedIP: string | null = null;
let ipPromise: Promise<string> | null = null;

// Anonymize IP (same logic as edge function)
const anonymizeIP = (ip: string): string => {
  if (!ip || ip === 'unknown') return 'unknown';

  // IPv6 detection and anonymization
  if (ip.includes(':')) {
    const parts = ip.split(':');
    return parts.slice(0, 4).join(':') + '::0';
  }

  // IPv4 anonymization - mask last octet
  const parts = ip.split('.');
  if (parts.length === 4) {
    return parts.slice(0, 3).join('.') + '.0';
  }

  return 'unknown';
};

// Fetch client IP
const getClientIP = async (): Promise<string> => {
  if (cachedIP) return cachedIP;
  if (ipPromise) return ipPromise;

  ipPromise = (async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      cachedIP = anonymizeIP(data.ip);
      return cachedIP;
    } catch (error) {
      cachedIP = 'unknown';
      return cachedIP;
    } finally {
      ipPromise = null;
    }
  })();

  return ipPromise;
};

/**
 * Hook that provides a placeholder for IP exclusion checking.
 * 
 * SECURITY NOTE: The actual IP exclusion check is done server-side in the 
 * track-click edge function using the service role key. This hook previously
 * queried the excluded_ips table directly, but that table is now admin-only
 * to prevent exposing which IP addresses belong to administrators.
 * 
 * This hook now only fetches the client IP for reference purposes.
 * The isExcluded value is always false client-side - actual exclusion
 * happens server-side in edge functions.
 */
export const useExcludedIP = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [currentIP, setCurrentIP] = useState<string | null>(null);

  useEffect(() => {
    // Fire-and-forget IP fetch - completely non-blocking
    const deferTimeout = setTimeout(() => {
      getClientIP()
        .then((ip) => {
          setCurrentIP(ip);
        })
        .catch(() => {
          // Silently fail
        });
    }, 1000); // Defer by 1s - no rush for this check

    return () => clearTimeout(deferTimeout);
  }, []);

  // Always return false - actual exclusion is done server-side
  // The excluded_ips table is admin-only for security
  return { isExcluded: false, isChecking };
};
